import { ClockIcon, CrossIcon } from 'assets/icons';
import Button from 'components/button';
import ChoiceChips from 'components/tasks/ChoiceChips';
import { TASK_TYPE } from 'constants/taskTypes';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import { clockLabel, isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { longDay } from 'pages/planning/planningItems';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

const TYPES = ['todo', 'meeting'];
const pad = (value) => String(value).padStart(2, '0');
// The cursor in the title at once, without scrolling the grid.
const focusOnMount = (element) => element?.focus({ preventScroll: true });

/**
 * The creation on a slot of the planning, as in Google Calendar: a title, the
 * type, the moment picked and a description. Entrée saves (⌘ or Ctrl + Entrée
 * in the description); Échap or a click elsewhere closes; « Plus d'options »
 * opens the whole task form. `draft`: { start, minutes, allDay, type, title,
 * note }. « Tâche » and « Rendez-vous » are two things, not two kinds of task:
 * a rendez-vous is an appointment of the planning and needs a time (the band
 * without a time only takes tasks), and has no « Plus d'options ».
 */
const QuickCreate = ({ draft, onChange, onClose, onSaved, onMore }) => {
  const { text } = useText('planning');
  const { text: tasksText } = useText('tasks');
  const { call, me } = useCrmContext();
  const notifyTasks = useNotifyTasksChanged();
  const box = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && onClose();
    const onDown = (event) => {
      if (!box.current?.contains(event.target)) onClose();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [onClose]);

  const title = draft.title.trim();
  const note = (draft.note ?? '').trim();
  const end = new Date(draft.start.getTime() + draft.minutes * 60000);

  const save = async () => {
    if (!title || busy) return;
    setBusy(true);
    try {
      if (draft.type === 'meeting') {
        const created = await call(crmApi.createAppointment, {
          title,
          description: note,
          startsAt: draft.start.toISOString(),
          endsAt: end.toISOString(),
          category: 'interne',
        });
        toast.success(text('meetingAdded'));
        onSaved({ kind: 'appointment', id: created.id, start: draft.start });
      } else {
        const created = await call(crmApi.createTask, {
          fields: {
            type: TASK_TYPE,
            title,
            note,
            dueOn: isoDay(draft.start),
            dueTime: draft.allDay ? null : `${pad(draft.start.getHours())}:${pad(draft.start.getMinutes())}`,
            durationMin: draft.minutes,
            assigneeId: me?.id,
          },
        });
        notifyTasks();
        toast.success(text('taskAdded'));
        onSaved({ kind: 'task', id: created.id, start: draft.start });
      }
    } catch (error) {
      const message = crmTaskErrorText(error, tasksText);
      if (message) toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={box} className="w-[340px] rounded-2xl border border-gray-100 bg-white p-4 shadow-lg">
      <div className="flex items-start gap-2">
        <input
          ref={focusOnMount}
          value={draft.title}
          onChange={(event) => onChange({ title: event.target.value })}
          onKeyDown={(event) => event.key === 'Enter' && save()}
          placeholder={text('addTitle')}
          aria-label={text('titleLabel')}
          className="min-w-0 flex-1 border-b-2 border-gray-200 bg-transparent pb-1.5 ft-lg text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-yellow-500"
        />
        <button type="button" aria-label={text('close')} onClick={onClose} className="rounded-md p-1 hover:bg-gray-100">
          <CrossIcon wh={18} color="#374151" />
        </button>
      </div>
      <div className="mt-3">
        <ChoiceChips
          options={(draft.allDay ? ['todo'] : TYPES).map((type) => [type, text(`type_${type}`)])}
          value={draft.type}
          onPick={(type) => onChange({ type })}
        />
      </div>
      <p className="mt-3 flex items-center gap-2 text-sm text-gray-700">
        <span className="flex shrink-0 text-gray-400">
          <ClockIcon wh={16} color="currentColor" />
        </span>
        {longDay(draft.start)} · {draft.allDay ? text('noTime') : `${clockLabel(draft.start)} – ${clockLabel(end)}`}
      </p>
      <textarea
        value={draft.note ?? ''}
        onChange={(event) => onChange({ note: event.target.value })}
        onKeyDown={(event) => (event.metaKey || event.ctrlKey) && event.key === 'Enter' && save()}
        rows={2}
        placeholder={text('addDescription')}
        aria-label={text('descriptionLabel')}
        className="mt-3 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm leading-5 text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-yellow-500"
      />
      <div className="mt-4 flex justify-end gap-2">
        {draft.type !== 'meeting' && (
          <Button color="SECONDARY" size="SM" onClick={onMore}>
            {text('moreOptions')}
          </Button>
        )}
        <Button color="PRIMARY" size="SM" disabled={!title || busy} onClick={save}>
          {busy ? text('saving') : text('save')}
        </Button>
      </div>
    </div>
  );
};

export default QuickCreate;
